/**
 * /zh/product-management-metrics — 产品管理指标中文 SEO 页。与
 * ProductManagementMetrics.tsx 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function ProductManagementMetricsZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/product-management-metrics"
      documentTitle="产品管理指标与 KPI · Draftlize"
      documentDescription="真正要紧的产品指标与 KPI——北极星、AARRR、激活、留存、SaaS 金字塔——各自衡量什么，以及怎么挑出契合你策略的那几个。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 产品指标</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            产品管理指标<br/>
            <em>——真正要紧的那几个。</em>
          </h1>
          <p className="lede">
            你能追踪的<em>产品管理 KPI</em> 有一百个，该追的也许只有五个。功夫不在「什么都量」，而在挑出那几个能告诉你「策略在不在奏效」的，并忽略那些只会上涨的虚荣数字。这页讲 PM 真正在用的指标框架——北极星、AARRR、SaaS 金字塔、领先 vs 滞后——各自衡量什么，以及怎么挑出契合你产品的那几个。然后是诚实的那部分：一个指标编码了「优化什么」的赌注，而当策略变了，指标也该变。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么多数指标<em>是噪声。</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            陷阱是虚荣指标——一个总在上涨（累计注册用户、页面浏览、累计下载）、却对「产品在不在奏效」什么都说不出来的数字。一个好的<em>产品经理 KPI</em>，是有决策挂在它上面的：如果它动了，你会做点不一样的事。如果不管数字怎样、都没有任何决策改变，那你是在为安心而量，不是为掌舵而量。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: 0 }}>
            另一个值得知道的区分是<strong>领先 vs 滞后</strong>。滞后指标（营收、流失）告诉你已经发生了什么；领先指标（激活率、周活）先动、并预测滞后的那些。你用领先指标掌舵，用滞后指标评判——一块只有滞后指标的仪表盘，是一面后视镜。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">几种框架</span>
            <h2 className="serif">PM 怎么<em>组织指标。</em></h2>
          </div>
          <p>几个框架把混乱理出结构。挑一个当主干——通常是一个北极星加一棵小小的输入树——而不是一次全都追。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: '北极星指标', hint: '那一个数字', body: '最能捕住你产品所交付价值的那个单一指标——订出的房晚、发出的消息、周活团队数。其余一切都是它的输入。它的力量是聚焦；它的危险是挑到一个奖励错误行为的，所以要像挑策略一样慎重地挑它。' },
            { k: 'AARRR（海盗指标）', hint: '漏斗', body: '获取、激活、留存、推荐、营收——用户走过的生命周期。最适合诊断你在哪漏：一个漂亮的获取数字配上孱弱的激活，正好告诉你在灌更多流量之前该先修哪一段。' },
            { k: '激活与留存', hint: '留不留得住', body: '激活（新用户有没有见到首次价值）和留存（他们会不会一直回来）这两个，预测着下游的一切。多数早期产品没有增长问题；它们有一个穿着增长外衣的留存问题。' },
            { k: 'SaaS 指标金字塔', hint: '生意', body: 'MRR/ARR、流失、LTV、CAC、回本周期——一个订阅生意的财务健康。它们多为滞后；用来评判产品和 GTM 在不在奏效，但你用它们上面那些领先的产品指标来掌舵。' },
            { k: 'HEART（体验质量）', hint: '体验', body: 'Happiness、Engagement、Adoption、Retention、Task success——Google 用来衡量体验质量、而不只是用量的框架。当问题是「这好不好」、而不只是「有没有人用」时有用。' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.3 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.04em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          你挑的指标是 <a href="/zh/product-strategy" style={{ color: 'var(--amber)' }}>产品策略</a> 的下游——它们衡量那些赌注有没有在兑现。
        </p>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">指标是一个赌注。<em>赌注变了，它也该变。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            挑一个北极星和它的输入树，是一个战略决策——你在宣告「奏效」意味着什么。当策略转向，那个宣告就过时了，而一个还在优化旧北极星的团队，正朝错的方向使劲划船。Draftlize 让指标连着它所服务的策略。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个指标连到它衡量的策略</h3>
            <p>在 Draftlize 里，你的北极星和它的输入是卡片，连着它们「为之而生、去追踪」的那个战略赌注。指标不是一块仪表盘上和「你为什么选它」脱钩的数字——它指向那个让它成为「该优化的对象」的决策。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">策略一变，指标标 stale</h3>
            <p>从自助转向企业，「周活用户」可能不再是对的北极星。因为指标连着策略，它在策略移动时自动标记 stale——于是你刻意地重选「优化什么」，而不是被一个悄悄不再对应目标的数字牵着走。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的团队和 agent 读到当前目标</h3>
            <p>每个人——以及经 MCP 帮着做分析的 AI agent——读到「活」的指标定义和它们的理由，于是一个上季度的北极星，不会在「为它正名的策略」早已走远后还在驱动路线图决策。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          虚荣指标无论如何都上涨。真正的 KPI 是有决策挂在它上面的——也是在它底下的策略变了时，就该跟着变的那一个。
        </blockquote>
        <cite>只追那几个要紧的，把每个连到它本该衡量的策略。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '最重要的产品管理指标有哪些？', a: '没有通用清单——取决于你的产品和阶段。但激活和留存几乎预测下游的一切，一个北极星指标让团队聚焦，对 SaaS 生意而言 MRR、流失、LTV、CAC 评判财务健康。挑一小组连着你策略的，而不是什么都追。' },
            { q: '什么是北极星指标？', a: '最能捕住你产品向用户交付之核心价值的那个单一指标——全团队优化的那一个数字，其余都当作它的输入。它的强项是聚焦；风险是挑到一个奖励错误行为的，所以该像挑策略一样慎重地挑它。' },
            { q: '领先指标和滞后指标有什么区别？', a: '滞后指标（营收、流失）衡量已经发生的事；领先指标（激活率、周活）先动、并预测滞后的那些。你用领先指标掌舵，用滞后指标评判结果。' },
            { q: '什么是虚荣指标？', a: '一个总在上涨、看起来好、却不为任何决策提供信息的数字——累计注册用户、累计下载、原始页面浏览。检验真 KPI 的标准是：它的变化会不会让你做不一样的事；如果不会，就是虚荣。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">追踪连着策略的指标<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            用你喜欢的任何分析栈去量。把北极星和它的理由放进 Draftlize、连着它所服务的策略——这样策略一动，指标就标 stale，而不是用一个不再对应目标的指标牵着团队走。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
